import { useMemo, useRef } from "react";
import { useXRHitTest } from "@react-three/xr";
import * as THREE from "three";
import { PlacementStabilityGate } from "@/runtime/placementStability";
import type { PlacementPose } from "@/runtime/types";

export function WebXRPlacement({ onPlacement, onProgress }: { onPlacement: (pose: PlacementPose) => void; onProgress: (progress: number) => void }) {
  const gate = useMemo(() => new PlacementStabilityGate(), []);
  const reticle = useRef<THREE.Group>(null);
  const matrix = useMemo(() => new THREE.Matrix4(), []);
  const position = useMemo(() => new THREE.Vector3(), []);
  const quaternion = useMemo(() => new THREE.Quaternion(), []);
  const scale = useMemo(() => new THREE.Vector3(), []);
  const accepted = useRef(false);

  useXRHitTest((results, getWorldMatrix) => {
    if (accepted.current || results.length === 0 || !results[0] || !getWorldMatrix(matrix, results[0])) {
      if (results.length === 0) onProgress(gate.observe(null, performance.now()).progress);
      return;
    }
    matrix.decompose(position, quaternion, scale);
    const pose: PlacementPose = {
      position: [position.x, position.y, position.z],
      quaternion: [quaternion.x, quaternion.y, quaternion.z, quaternion.w],
      scale: 0.55,
    };
    reticle.current?.position.copy(position);
    reticle.current?.quaternion.copy(quaternion);
    const result = gate.observe(pose, performance.now());
    onProgress(result.progress);
    if (result.accepted) {
      accepted.current = true;
      onPlacement(result.accepted);
    }
  }, "viewer", "plane");

  return (
    <group ref={reticle} rotation={[-Math.PI / 2, 0, 0]}>
      <mesh>
        <ringGeometry args={[0.08, 0.1, 48]} />
        <meshBasicMaterial color="#63edff" transparent opacity={0.9} side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}
